import {
  Layers,
  Code2,
  Cloud,
  Sparkles,
  Lightbulb,
  Target,
  Shield,
  Users,
  Gem,
  type LucideIcon,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { AboutReveal, AboutStagger, AboutStaggerItem } from "./about-reveal";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { whoWeAreCopy } from "@/lib/aboutPageCopy";

const capabilityIcons: LucideIcon[] = [Layers, Code2, Cloud, Sparkles];
const valueIcons: LucideIcon[] = [Lightbulb, Target, Gem, Shield, Users];

function ValuesLogoMark() {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none absolute bottom-6 -right-3 md:bottom-10 md:-right-1"
      initial={reduceMotion ? false : { opacity: 0, y: 14 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      animate={
        reduceMotion
          ? undefined
          : {
              y: [0, -12, 0],
              rotate: [0, 2.5, 0],
            }
      }
      transition={{
        opacity: { duration: 0.65, ease: [0.23, 1, 0.32, 1] },
        y: {
          duration: 5.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.55,
        },
        rotate: {
          duration: 5.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.55,
        },
      }}
    >
      <img
        src="/assets/DevDotCom_logo_without_bg.png"
        alt=""
        className="h-36 w-auto opacity-[0.09] md:h-48 md:opacity-[0.11] dark:opacity-[0.14] dark:md:opacity-[0.16]"
      />
    </motion.div>
  );
}

export function WhoWeAre() {
  const lang = useSiteLanguage();
  const t = whoWeAreCopy[lang];

  return (
    <section
      id="who-we-are"
      className="scroll-mt-24 bg-background pb-20 md:pb-28 lg:pb-32"
    >
      <div className="mx-auto grid max-w-6xl items-stretch gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-14">
        <AboutReveal className="flex flex-col justify-center">
          <span className="mb-4 block text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            {t.eyebrow}
          </span>
          <h2 className="max-w-md text-balance text-2xl font-bold tracking-tight text-foreground md:text-3xl lg:text-[2.15rem]">
            {t.title}
          </h2>
          <div className="mt-6 max-w-lg space-y-4 text-[15px] leading-relaxed text-muted-foreground md:text-base">
            <p>{t.p1}</p>
            <p>{t.p2}</p>
            <p className="hidden sm:block">{t.p3}</p>
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-4 md:mt-10">
            {t.capabilities.map((label, index) => {
              const Icon = capabilityIcons[index];
              return (
                <li
                  key={label}
                  className="inline-flex items-center gap-2 text-sm text-foreground/80"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-muted text-foreground">
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </span>
                  {label}
                </li>
              );
            })}
          </ul>
        </AboutReveal>

        <AboutReveal delay={0.1}>
          <div className="relative h-full overflow-hidden rounded-[1.75rem] border border-border bg-muted/60 p-7 md:rounded-[2rem] md:p-9 lg:p-10">
            <h3 className="relative z-10 mb-7 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              {t.valuesTitle}
            </h3>
            <AboutStagger className="relative z-10 space-y-5 md:space-y-6">
              {t.values.map((value, index) => {
                const Icon = valueIcons[index];
                return (
                  <AboutStaggerItem key={value.title}>
                    <div className="flex items-start gap-3.5">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-foreground shadow-sm">
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                      </span>
                      <div>
                        <h4 className="text-sm font-semibold tracking-tight text-foreground md:text-[15px]">
                          {value.title}
                        </h4>
                        <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </AboutStaggerItem>
                );
              })}
            </AboutStagger>
            <ValuesLogoMark />
          </div>
        </AboutReveal>
      </div>
    </section>
  );
}
